import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import RequestClassificationField, { classificationIsCui } from '../components/RequestClassificationField';
import SchemaBodyFields from '../components/SchemaBodyFields';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import {
  fieldName,
  fieldType,
  fileList,
  normalizeValuesForSubmit,
} from '../utils/requestContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function matchColor(level) {
  if (level === 'strong_match') {
    return 'success';
  }
  if (level === 'not_compatible') {
    return 'error';
  }
  return 'primary';
}

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

export default function DiscoveryRequestPage(props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [selectingCapabilityId, setSelectingCapabilityId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [schemaContract, setSchemaContract] = useState({});
  const [matches, setMatches] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedContract, setSelectedContract] = useState({});
  const [sharedValues, setSharedValues] = useState({});
  const [additionalValues, setAdditionalValues] = useState({});
  const [fileValues, setFileValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState({
    requesting_business_unit_id: '',
    classification: '',
    title: '',
    description: '',
  });

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [categoryResponse, businessUnitResponse] = await Promise.all([
          axiosInstance.get('/requests/need-categories'),
          axiosInstance.get('/requests/business-units'),
        ]);
        if (!isMounted) {
          return;
        }
        const nextCategories = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
        const nextBusinessUnits = Array.isArray(businessUnitResponse.data?.business_units)
          ? businessUnitResponse.data.business_units
          : [];
        setCategories(nextCategories);
        setBusinessUnits(nextBusinessUnits);
        setForm((current) => ({
          ...current,
          requesting_business_unit_id: current.requesting_business_unit_id
            || (nextBusinessUnits.length === 1 ? nextBusinessUnits[0].business_unit_id : ''),
        }));
      } catch (loadError) {
        if (isMounted) {
          setError(errorMessage(loadError, 'Unable to load discovery request options.'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setSchemaContract({});
    setMatches(null);
    setSelectedMatch(null);
    setSelectedContract({});
    setSharedValues({});
    setAdditionalValues({});
    setFileValues({});
    setFormErrors({});

    if (!categoryId) {
      return () => {
        isMounted = false;
      };
    }

    async function loadSchema() {
      setSchemaLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get(
          `/requests/discovery/${encodeURIComponent(categoryId)}/schema`,
        );
        if (!isMounted) {
          return;
        }
        const nextContract = response.data || {};
        setSchemaContract(nextContract);
        setForm((current) => ({
          ...current,
          title: current.title || `Request ${nextContract.need_category?.category_display_name || 'Capability'}`,
        }));
      } catch (schemaError) {
        if (isMounted) {
          setError(errorMessage(schemaError, 'Unable to load the Need Category request form.'));
        }
      } finally {
        if (isMounted) {
          setSchemaLoading(false);
        }
      }
    }

    loadSchema();
    return () => {
      isMounted = false;
    };
  }, [categoryId]);

  const sharedFields = useMemo(
    () => schemaContract?.effective_request_schema?.shared_fields || schemaContract?.shared_fields || [],
    [schemaContract],
  );
  const additionalFields = useMemo(
    () => selectedContract?.effective_request_schema?.additional_fields || [],
    [selectedContract],
  );

  const updateValue = (setter) => (field, value) => {
    const name = fieldName(field);
    setter((current) => ({ ...current, [name]: value }));
  };

  const updateFileValue = (field, value) => {
    const name = fieldName(field);
    setFileValues((current) => ({ ...current, [name]: value }));
  };

  const clearProviderFileValues = () => {
    const sharedFieldNames = new Set(sharedFields.map((field) => fieldName(field)).filter(Boolean));
    setFileValues((current) => Object.fromEntries(
      Object.entries(current).filter(([name]) => sharedFieldNames.has(name)),
    ));
  };

  const validateDiscoveryForm = () => {
    const nextErrors = {};
    if (!categoryId) {
      nextErrors.category_id = 'What you need is required.';
    }
    if (!form.requesting_business_unit_id) {
      nextErrors.requesting_business_unit_id = 'Requesting Business Unit is required.';
    }
    if (!form.classification) {
      nextErrors.classification = 'Content Classification is required.';
    }
    if (!form.title.trim()) {
      nextErrors.title = 'Request Title is required.';
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const findMatches = async () => {
    if (!validateDiscoveryForm()) {
      return;
    }
    setMatching(true);
    setError('');
    try {
      const response = await axiosInstance.post(
        `/requests/discovery/${encodeURIComponent(categoryId)}/matches`,
        {
          requesting_business_unit_id: form.requesting_business_unit_id,
          shared_request_values: normalizeValuesForSubmit(sharedFields, sharedValues),
        },
      );
      setMatches(Array.isArray(response.data?.matching_capabilities) ? response.data.matching_capabilities : []);
      setSelectedMatch(null);
      setSelectedContract({});
    } catch (matchError) {
      setError(errorMessage(matchError, 'Unable to match capabilities.'));
    } finally {
      setMatching(false);
    }
  };

  const selectCapability = async (match) => {
    setSelectingCapabilityId(match.capability_definition_id);
    setError('');
    try {
      const response = await axiosInstance.get(match.request_schema_path);
      setSelectedMatch(match);
      setSelectedContract(response.data || {});
      setAdditionalValues({});
      clearProviderFileValues();
    } catch (selectionError) {
      setError(errorMessage(selectionError, 'Unable to load provider-specific request fields.'));
    } finally {
      setSelectingCapabilityId('');
    }
  };

  const uploadFilesForFields = async (fields, source) => {
    const uploaded = {};
    for (const field of fields) {
      if (fieldType(field) !== 'file') {
        continue;
      }
      const name = fieldName(field);
      const files = fileList(fileValues[name]);
      if (!files.length) {
        continue;
      }
      const artifactIds = [];
      for (const file of files) {
        const uploadResponse = await axiosInstance.post(
          `/capabilities/${encodeURIComponent(selectedMatch.capability_definition_id)}/request/upload`,
          {
            requesting_business_unit_id: form.requesting_business_unit_id,
            is_cui: classificationIsCui(form.classification),
            field_key: name,
            source,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
          },
        );
        const uploadUrl = uploadResponse.data?.upload_url;
        if (!uploadUrl) {
          throw new Error('Upload URL was not returned.');
        }
        const uploadResult = await fetch(uploadUrl, {
          method: uploadResponse.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!uploadResult.ok) {
          throw new Error(`Upload failed for ${file.name}.`);
        }
        artifactIds.push(uploadResponse.data?.artifact_id || uploadResponse.data?.file_id);
      }
      uploaded[name] = field.multiple ? artifactIds : artifactIds[0];
    }
    return uploaded;
  };

  const submitRequest = async () => {
    if (!selectedMatch || !validateDiscoveryForm()) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const sharedUploads = await uploadFilesForFields(sharedFields, 'shared');
      const additionalUploads = await uploadFilesForFields(additionalFields, 'capability');
      const response = await axiosInstance.post(selectedMatch.request_path, {
        requesting_business_unit_id: form.requesting_business_unit_id,
        is_cui: classificationIsCui(form.classification),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        shared_request_values: normalizeValuesForSubmit(sharedFields, sharedValues, sharedUploads),
        capability_request_values: normalizeValuesForSubmit(additionalFields, additionalValues, additionalUploads),
        metadata: {
          selection_path: 'discovery',
          discovery_category_id: categoryId,
          discovery_match_level: selectedMatch.match_level,
          discovery_match_score: selectedMatch.score,
        },
      });
      const requestCapabilityId = response.data?.request_capability_id;
      if (!requestCapabilityId) {
        throw new Error('Request was submitted, but the request id was missing.');
      }
      navigate(`/requests/direct/${requestCapabilityId}`);
    } catch (submitError) {
      setError(errorMessage(submitError, 'Unable to submit request.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetToNeed = () => {
    setMatches(null);
    setSelectedMatch(null);
    setSelectedContract({});
    setAdditionalValues({});
    clearProviderFileValues();
    setError('');
  };

  const returnToMatches = () => {
    setSelectedMatch(null);
    setSelectedContract({});
    setAdditionalValues({});
    setError('');
  };

  const renderNeedForm = () => (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>What do you need?</Typography>
          <TextField
            select
            label="Need Category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
            fullWidth
            disabled={matching}
            error={Boolean(formErrors.category_id)}
            helperText={formErrors.category_id || 'Choose the kind of work AMX should match.'}
          >
            {categories.map((category) => (
              <MenuItem key={category.category_id} value={category.category_id}>
                {category.category_display_name || category.category_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Requesting Business Unit"
            value={form.requesting_business_unit_id}
            onChange={(event) => setForm((current) => ({ ...current, requesting_business_unit_id: event.target.value }))}
            required
            fullWidth
            disabled={matching || businessUnits.length === 1}
            error={Boolean(formErrors.requesting_business_unit_id)}
            helperText={formErrors.requesting_business_unit_id || ' '}
          >
            {businessUnits.map((unit) => (
              <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                {businessUnitLabel(unit)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {schemaLoading ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography>Loading request form...</Typography>
          </Stack>
        </Paper>
      ) : categoryId && sharedFields.length ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Need Details</Typography>
            <SchemaBodyFields
              fields={sharedFields}
              values={sharedValues}
              fileValues={fileValues}
              disabled={matching}
              onChange={updateValue(setSharedValues)}
              onFileChange={updateFileValue}
            />
          </Stack>
        </Paper>
      ) : null}

      {categoryId && !schemaLoading ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Request Details</Typography>
            <RequestClassificationField
              value={form.classification}
              onChange={(event) => setForm((current) => ({ ...current, classification: event.target.value }))}
              disabled={matching}
              error={Boolean(formErrors.classification)}
              helperText={formErrors.classification || 'Select whether this request contains Controlled Unclassified Information.'}
            />
            <TextField
              label="Request Title"
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              required
              fullWidth
              disabled={matching}
              error={Boolean(formErrors.title)}
              helperText={formErrors.title || ' '}
            />
            <TextField
              label="Additional Notes"
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              multiline
              minRows={4}
              fullWidth
              disabled={matching}
            />
            <Divider />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SearchRoundedIcon />}
                disabled={matching || schemaLoading || !schemaContract.schema_id}
                onClick={findMatches}
              >
                {matching ? 'Matching...' : 'Find Matching Capabilities'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );

  const renderMatches = () => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Matching Capabilities</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a compatible capability. Strong matches cover every supplied matching field.
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={resetToNeed}>Edit Need</Button>
      </Stack>

      {!matches.length ? (
        <Alert severity="info">No requestable capabilities are available for this Need Category.</Alert>
      ) : matches.map((match) => {
        const providerName = businessUnitLabel(match.provider_business_unit);
        const reason = match.incompatible_fields?.[0]?.reason || match.uncovered_fields?.[0]?.reason;
        return (
          <Paper key={match.capability_definition_id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{match.capability_name}</Typography>
                  <Chip size="small" color={matchColor(match.match_level)} label={match.match_label} />
                  {match.match_level !== 'not_compatible' && match.considered_field_count ? (
                    <Chip size="small" variant="outlined" label={`${match.score}% coverage`} />
                  ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {providerName || 'Unknown provider'}
                </Typography>
                {match.description ? <Typography sx={{ mt: 1 }}>{match.description}</Typography> : null}
                {reason ? (
                  <Typography variant="body2" color={match.match_level === 'not_compatible' ? 'error.main' : 'text.secondary'} sx={{ mt: 1 }}>
                    {reason}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <Button
                  variant={match.match_level === 'strong_match' ? 'contained' : 'outlined'}
                  startIcon={match.match_level === 'strong_match' ? <CheckCircleRoundedIcon /> : null}
                  disabled={match.match_level === 'not_compatible' || Boolean(selectingCapabilityId)}
                  onClick={() => selectCapability(match)}
                >
                  {selectingCapabilityId === match.capability_definition_id ? 'Loading...' : 'Select'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );

  const renderSelectedCapability = () => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedMatch.capability_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {businessUnitLabel(selectedMatch.provider_business_unit)}
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={returnToMatches}>Change Capability</Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip color={matchColor(selectedMatch.match_level)} label={selectedMatch.match_label} />
            <Typography variant="body2" color="text.secondary">
              Selected through capability discovery
            </Typography>
          </Stack>
          <Divider />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Provider-Specific Details</Typography>
          {additionalFields.length ? (
            <SchemaBodyFields
              fields={additionalFields}
              values={additionalValues}
              fileValues={fileValues}
              disabled={submitting}
              onChange={updateValue(setAdditionalValues)}
              onFileChange={updateFileValue}
            />
          ) : (
            <Alert severity="success">This provider does not require any additional request fields.</Alert>
          )}
          <Divider />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SendRoundedIcon />}
              disabled={submitting}
              onClick={submitRequest}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Requests', href: '/requests' },
              { label: 'Discover' },
            ]}
          />
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>Discover a Capability</Typography>
              <Typography variant="body2" color="text.secondary">
                Describe the need first, compare matching providers, then submit the same request used by the direct flow.
              </Typography>
            </Box>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {form.classification ? (
              <ClassificationBanner isCui={classificationIsCui(form.classification)} />
            ) : null}

            {loading ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={22} />
                  <Typography>Loading discovery request...</Typography>
                </Stack>
              </Paper>
            ) : selectedMatch ? renderSelectedCapability() : matches !== null ? renderMatches() : renderNeedForm()}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
